'use client';

// ОДНА ВКЛАДКА СПРАШИВАЕТ /api/version ЗА ВСЕ (ТЗ SPEED §8.1, §8.3).
//
// Минутный опрос версии сайта (components/pwa-updater.tsx, POLL_MS) шёл из КАЖДОЙ открытой
// вкладки браузера: пять вкладок кабинета — пять запросов в минуту. Теперь вкладки выбирают
// лидера: ключ `localStorage['scriptera:version-leader'] = {id, at}`; лидер обновляет `at`
// каждые 30 с и один ведёт минутный опрос, а итог («на сервере версия X») рассылает остальным
// через `BroadcastChannel('scr-version')`. Лидер пропал (вкладку закрыли, браузер усыпил) —
// `at` старше 90 с, и лидерство берёт следующая вкладка.
//
// Сверка на старте, на возврате во вкладку и на pageshow остаётся у КАЖДОЙ вкладки — это не
// опрос, а событие самой вкладки. Нет localStorage или BroadcastChannel (приватный режим,
// старый WebKit, WKWebView без них) — вкладка считает лидером себя: поведение прежнее.

export const LEADER_KEY = 'scriptera:version-leader';
export const VERSION_CHANNEL = 'scr-version';
/** Как часто лидер подтверждает себя. */
export const LEADER_BEAT_MS = 30_000;
/** Через сколько молчания лидерство перехватывается. */
export const LEADER_STALE_MS = 90_000;

export interface LeaderRecord {
  id: string;
  at: number;
}

export function parseLeader(raw: string | null): LeaderRecord | null {
  if (!raw) return null;
  try {
    const j = JSON.parse(raw) as Partial<LeaderRecord>;
    return typeof j?.id === 'string' && typeof j.at === 'number' && Number.isFinite(j.at) ? { id: j.id, at: j.at } : null;
  } catch {
    return null;
  }
}

/** Брать ли лидерство: записи нет/битая, она моя или лидер молчит дольше `staleMs`. */
export function shouldLead(cur: LeaderRecord | null, myId: string, now: number, staleMs = LEADER_STALE_MS): boolean {
  if (!cur) return true;
  if (cur.id === myId) return true;
  // Часы ушли назад (запись «из будущего» дальше окна) — тоже считаем протухшей.
  return now - cur.at > staleMs || cur.at - now > staleMs;
}

export interface VersionLeader {
  /** Эта вкладка сейчас ведёт опрос? */
  isLeader: () => boolean;
  /** Лидер: разослать итог опроса остальным вкладкам. */
  share: (version: string | null) => void;
  stop: () => void;
}

/**
 * Включить выборы лидера. `onVersion` — итог опроса, присланный лидером другой вкладки.
 * Вызывается один раз на документ (эффект PwaUpdater).
 */
export function startVersionLeader(onVersion: (version: string) => void): VersionLeader {
  const myId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  let channel: BroadcastChannel | null = null;
  try {
    channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(VERSION_CHANNEL) : null;
  } catch {
    channel = null;
  }
  // Без канала итог не разослать — каждая вкладка сама себе лидер (прежнее поведение).
  let solo = !channel;
  let leading = solo;

  const beat = (): void => {
    if (solo) return;
    try {
      const now = Date.now();
      if (shouldLead(parseLeader(localStorage.getItem(LEADER_KEY)), myId, now)) {
        localStorage.setItem(LEADER_KEY, JSON.stringify({ id: myId, at: now } satisfies LeaderRecord));
        leading = true;
      } else {
        leading = false;
      }
    } catch {
      solo = true; // localStorage закрыт — лидер сам себе
      leading = true;
    }
  };

  if (channel) {
    channel.onmessage = (e: MessageEvent<unknown>) => {
      const v = (e.data as { version?: unknown } | null)?.version;
      if (typeof v === 'string' && v) onVersion(v);
    };
  }
  beat();
  const timer = window.setInterval(beat, LEADER_BEAT_MS);
  // Вкладку закрывают — отдаём лидерство сразу, не через 90 с.
  const release = (): void => {
    if (!leading || solo) return;
    try {
      if (parseLeader(localStorage.getItem(LEADER_KEY))?.id === myId) localStorage.removeItem(LEADER_KEY);
    } catch {
      /* хранилище закрыто — запись протухнет сама */
    }
    leading = false;
  };
  window.addEventListener('pagehide', release);

  return {
    isLeader: () => leading,
    share(version) {
      if (!channel || !version) return;
      try {
        channel.postMessage({ version });
      } catch {
        /* канал закрыт — остальные спросят сами, когда возьмут лидерство */
      }
    },
    stop() {
      window.clearInterval(timer);
      window.removeEventListener('pagehide', release);
      release();
      channel?.close();
      channel = null;
    },
  };
}
